// Application configuration based on environment

// Browser-safe environment detection
const getEnvironment = (): 'development' | 'production' | 'test' => {
    // In browser, check hostname to determine environment
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
            return 'development';
        }
        // Production environment
        return 'production';
    }
    // Fallback for SSR or Node.js context
    return 'production';
};

const ENVIRONMENT = getEnvironment();

// Configuration constants
const CONFIG_CONSTANTS = {
    API: {
        DEFAULT_TIMEOUT: 30000, // 30 seconds
        DEFAULT_BASE_URL: '/api',
    },
    CACHE: {
        STALE_TIME: 5 * 60 * 1000, // 5 minutes
        CACHE_TIME: 10 * 60 * 1000, // 10 minutes
        RETRY_ATTEMPTS: 2,
    },
    FILE_UPLOAD: {
        MAX_SIZE_BYTES: 50 * 1024 * 1024, // 50MB
        ABSOLUTE_MAX_SIZE_BYTES: 100 * 1024 * 1024, // 100MB
        PROGRESS_CLEAR_DELAY_MS: 5000, // 5 seconds
        UPLOAD_PROGRESS_STAGES: {
            UPLOADING: 10,
            EXTRACTING: 60,
            PROCESSING: 80,
            COMPLETE: 100,
        },
    },
    ANALYSIS: {
        POLLING_INTERVAL_MS: 5000, // 5 seconds
        MIN_POLLING_INTERVAL_MS: 1000, // 1 second
        MAX_CONCURRENT_PROCESSING: 30,
    },
} as const;

// Configuration validation with graceful fallbacks
const validateConfig = () => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate API configuration
    const apiBaseUrl = CONFIG_CONSTANTS.API.DEFAULT_BASE_URL;
    if (!apiBaseUrl.startsWith('/') && !apiBaseUrl.startsWith('http')) {
        errors.push('API base URL must be a valid URL or path');
    }

    // Log configuration for debugging
    console.log('AI DTR Configuration:', {
        mode: ENVIRONMENT,
        apiBaseUrl,
        isDev: ENVIRONMENT === 'development',
        isProd: ENVIRONMENT === 'production',
    });

    // Validate numeric values using constants
    if (CONFIG_CONSTANTS.ANALYSIS.POLLING_INTERVAL_MS < CONFIG_CONSTANTS.ANALYSIS.MIN_POLLING_INTERVAL_MS) {
        warnings.push(`Polling interval ${CONFIG_CONSTANTS.ANALYSIS.POLLING_INTERVAL_MS}ms is below recommended minimum ${CONFIG_CONSTANTS.ANALYSIS.MIN_POLLING_INTERVAL_MS}ms`);
    }

    if (CONFIG_CONSTANTS.FILE_UPLOAD.MAX_SIZE_BYTES > CONFIG_CONSTANTS.FILE_UPLOAD.ABSOLUTE_MAX_SIZE_BYTES) {
        errors.push(`Max file size ${CONFIG_CONSTANTS.FILE_UPLOAD.MAX_SIZE_BYTES} exceeds absolute limit ${CONFIG_CONSTANTS.FILE_UPLOAD.ABSOLUTE_MAX_SIZE_BYTES}`);
    }

    // Log warnings in all environments
    if (warnings.length > 0) {
        console.warn('Configuration warnings:', warnings);
    }

    // Handle errors based on environment
    if (errors.length > 0) {
        const errorMessage = `Configuration errors: ${errors.join(', ')}`;
        console.error(errorMessage);

        if (ENVIRONMENT === 'production') {
            // In production, use fallback values instead of crashing
            console.error('Using fallback configuration values');
            return false; // Indicate validation failed but continue
        } else {
            throw new Error(errorMessage);
        }
    }

    return true;
};

// Validate configuration on module load
validateConfig();

export const APP_CONFIG = {
    // API Configuration
    api: {
        baseUrl: CONFIG_CONSTANTS.API.DEFAULT_BASE_URL,
        timeout: CONFIG_CONSTANTS.API.DEFAULT_TIMEOUT,
    },

    // React Query Configuration
    queryClient: {
        staleTime: CONFIG_CONSTANTS.CACHE.STALE_TIME,
        cacheTime: CONFIG_CONSTANTS.CACHE.CACHE_TIME,
        retryAttempts: CONFIG_CONSTANTS.CACHE.RETRY_ATTEMPTS,
    },

    // File Upload Limits
    fileUpload: {
        maxSizeBytes: CONFIG_CONSTANTS.FILE_UPLOAD.MAX_SIZE_BYTES,
        progressClearDelayMs: CONFIG_CONSTANTS.FILE_UPLOAD.PROGRESS_CLEAR_DELAY_MS,
        progressStages: CONFIG_CONSTANTS.FILE_UPLOAD.UPLOAD_PROGRESS_STAGES,
        supportedFileTypes: {
            documents: {
                '.pdf': 'application/pdf',
                '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                '.doc': 'application/msword',
            },
            spreadsheets: {
                '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                '.xls': 'application/vnd.ms-excel',
            },
            email: {
                '.msg': 'application/vnd.ms-outlook',
            },
            images: {
                '.jpg': 'image/jpeg',
                '.jpeg': 'image/jpeg',
                '.png': 'image/png',
                '.tiff': 'image/tiff',
                '.bmp': 'image/bmp',
            },
            text: {
                '.txt': 'text/plain',
                '.md': 'text/markdown',
                '.csv': 'text/csv',
            },
        } as const,
        // Computed properties for backward compatibility
        get supportedTypes() {
            return Object.values(this.supportedFileTypes)
                .flatMap(category => Object.values(category));
        },
        get acceptedFileExtensions() {
            return Object.values(this.supportedFileTypes)
                .flatMap(category => Object.keys(category))
                .join(',');
        },
    },

    // Analysis Configuration
    analysis: {
        pollingIntervalMs: CONFIG_CONSTANTS.ANALYSIS.POLLING_INTERVAL_MS,
        maxConcurrentProcessing: CONFIG_CONSTANTS.ANALYSIS.MAX_CONCURRENT_PROCESSING,
    },

    // Environment flags
    isDevelopment: ENVIRONMENT === 'development',
    isProduction: ENVIRONMENT === 'production',
    isTesting: ENVIRONMENT === 'test',

    // Feature flags
    features: {
        enableDevTools: ENVIRONMENT === 'development',
        enableErrorReporting: ENVIRONMENT === 'production',
        enablePerformanceMonitoring: ENVIRONMENT === 'development',
    },

    // Authorization Configuration - skip in Website (handled by Katal)
    authorization: {
        bindleLockId: 'amzn1.bindle.resource.fsvovwtwdrl6scfcx5ma',
        skipInDevelopment: true, // Always skip in Website
    },
} as const;

export type AppConfig = typeof APP_CONFIG;

// File type utilities
export const FileTypeUtils = {
    /**
     * Check if a file extension is supported
     */
    isExtensionSupported(extension: string): boolean {
        const normalizedExt = extension.toLowerCase().startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`;
        return Object.values(APP_CONFIG.fileUpload.supportedFileTypes)
            .some(category => Object.keys(category).includes(normalizedExt));
    },

    /**
     * Get MIME type for a file extension
     */
    getMimeTypeForExtension(extension: string): string | null {
        const normalizedExt = extension.toLowerCase().startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`;
        for (const category of Object.values(APP_CONFIG.fileUpload.supportedFileTypes)) {
            if (normalizedExt in category) {
                return category[normalizedExt as keyof typeof category];
            }
        }
        return null;
    },

    /**
     * Get file category for an extension
     */
    getFileCategory(extension: string): string | null {
        const normalizedExt = extension.toLowerCase().startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`;
        for (const [categoryName, category] of Object.entries(APP_CONFIG.fileUpload.supportedFileTypes)) {
            if (Object.keys(category).includes(normalizedExt)) {
                return categoryName;
            }
        }
        return null;
    },

    /**
     * Get human-readable file type description
     */
    getFileTypeDescription(extension: string): string {
        const category = this.getFileCategory(extension);
        const categoryDescriptions: Record<string, string> = {
            documents: 'Document',
            spreadsheets: 'Spreadsheet',
            email: 'Email',
            images: 'Image',
            text: 'Text File',
        };
        return categoryDescriptions[category || ''] || 'Unknown File Type';
    },

    /**
     * Validate file against size and type constraints
     */
    validateFile(file: File): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Check file size
        if (file.size > APP_CONFIG.fileUpload.maxSizeBytes) {
            const maxSizeMB = Math.round(APP_CONFIG.fileUpload.maxSizeBytes / (1024 * 1024));
            errors.push(`File size exceeds ${maxSizeMB}MB limit`);
        }

        // Check file type
        const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
        if (!this.isExtensionSupported(extension)) {
            errors.push(`File type ${extension} is not supported`);
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    },
} as const;
